/**
 * Spec 055, step 3: the settings window opens with this — what the agent may do in a phrase, what
 * its unit's kit gives (folded to a line), what only this agent has (risky rights and exceptions,
 * in the warning colour), the limits and the model in a line. Read only: the server builds every
 * word of it from the sum, by templates, no model.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import type { AgentSettingsOut } from "@/api/types";
import { Caption } from "@/kit";
import { cn } from "@/lib/cn";

const SIGN = { "!": "!", "+": "+", "-": "−" } as const;

export function AgentSettingsSummary({ settings }: { settings: AgentSettingsOut }) {
  const [open, setOpen] = useState(false);
  const unit = settings.from_unit;
  const run = settings.last_run;
  return (
    <div className="flex flex-col gap-4" data-settings-summary>
      <p className="text-body text-p-text">{settings.phrase}</p>

      <section className="space-y-1.5" data-from-unit>
        {unit ? (
          <>
            <button type="button" className="flex w-full items-start justify-between gap-2 text-left" aria-expanded={open}
              onClick={() => setOpen(!open)}>
              <span className="min-w-0 flex-1">
                <span className="block text-body font-semibold text-p-text">От отдела «{unit.unit_name}»</span>
                <span className="block text-meta text-p-text-muted">{unit.line}</span>
              </span>
              <span className="text-meta text-p-link">{open ? "свернуть" : "раскрыть"}</span>
            </button>
            {open && (
              <div className="space-y-1">
                <ul className="space-y-0.5 text-meta text-p-text">
                  {unit.lines.map((line) => (
                    <li key={line}>
                      · {line}
                      {unit.also_in_card.includes(line) && <span className="text-p-text-muted"> — есть и в карточке</span>}
                    </li>
                  ))}
                </ul>
                <p className="text-micro text-p-text-muted">
                  {unit.kits.map((k) => `«${k.unit_name}», версия ${k.version}`).join(" · ")}
                  {" · "}
                  <Link to={`/departments/${unit.unit_id}?tab=kit`} className="font-semibold text-p-link">изменить набор</Link>
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-meta text-p-text-muted">Набора отдела нет — всё, что агент может, дано в карточке.</p>
        )}
      </section>

      <section className="space-y-1.5 rounded-card border border-p-border bg-p-surface-2 px-4 py-3" data-only-this-agent>
        <Caption>Только у этого агента</Caption>
        {settings.only.length ? (
          <ul className="space-y-0.5 text-meta">
            {settings.only.map((line, i) => (
              <li key={i} className={cn(line.stale ? "text-p-text-muted" : line.sign === "!" ? "text-p-needs" : "text-p-text")}>
                {SIGN[line.sign]} {line.text}
                {line.stale && " — нет в наборе"}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-meta text-p-text-muted">Ничего сверх набора.</p>
        )}
      </section>

      <section className="space-y-0.5" data-limits-model>
        <Caption>Лимиты и модель</Caption>
        <p className="text-meta text-p-text">{settings.limits}</p>
        <p className="text-meta text-p-text">{settings.model}</p>
        {run && (
          <p className="text-meta text-p-text-muted">
            Последний запуск: {[run.model, run.effort].filter(Boolean).join(", ") || run.runtime}
          </p>
        )}
      </section>
    </div>
  );
}
